import type { ChatStatus, UIMessage } from 'ai';

/** Tool states that mean the call has settled and its chip stopped spinning. */
const SETTLED_TOOL_STATES: ReadonlySet<string> = new Set(['output-available', 'output-error']);

function isToolPart(part: UIMessage['parts'][number]): boolean {
  return part.type.startsWith('tool-') || part.type === 'dynamic-tool';
}

/**
 * Whether the assistant is working with nothing on screen to show for it:
 * the request is sent but no token has arrived, or a tool has finished and
 * the next step hasn't produced text yet. A running tool already shows its
 * own spinning chip, and streaming text is its own progress, so neither
 * counts.
 *
 * @param status - The `useChat` status.
 * @param messages - The live conversation.
 * @returns True when a "Thinking…" indicator should show.
 */
export function isAwaitingReply(status: ChatStatus, messages: readonly UIMessage[]): boolean {
  if (status === 'submitted') return true;
  if (status !== 'streaming') return false;

  const last = messages.at(-1);
  if (last === undefined || last.role !== 'assistant') return true;

  const lastPart = [...last.parts].reverse().find((part) => part.type !== 'step-start');
  if (lastPart === undefined) return true;
  if (lastPart.type === 'text') return lastPart.text.trim().length === 0;
  if (isToolPart(lastPart)) {
    return SETTLED_TOOL_STATES.has((lastPart as { state?: string }).state ?? '');
  }
  return true;
}
